import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { AdvancedMarker, InfoWindow } from "@vis.gl/react-google-maps";
import { GoogleMapShell, GTrafficLayer, GPolyline, FitBounds, NumPin, WarnPin, VanPin, WARN_COLORS, WARN_LETTER } from "@/components/GoogleRouteMap";
import api, { errMsg, fmtDist, fmtDur } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Warehouse, Trash2, Navigation, Printer, GripVertical, Share2, TrafficCone, RefreshCw, Camera, PenLine, ChevronUp, ChevronDown } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";

export default function RoutePlannerPage() {
  const { theme } = useTheme();
  const { canEdit } = useAuth();
  const editable = canEdit("routes");
  const [yard, setYard] = useState(null);
  const [yardForm, setYardForm] = useState({ name: "", postcode: "" });
  const [savingYard, setSavingYard] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [stores, setStores] = useState([]);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [vehicleId, setVehicleId] = useState("");
  const [selected, setSelected] = useState([]);
  const [storeFilter, setStoreFilter] = useState("");
  const [optimizing, setOptimizing] = useState(false);
  const [route, setRoute] = useState(null);
  const [savedRoutes, setSavedRoutes] = useState([]);
  const [dragIdx, setDragIdx] = useState(null);
  const [reordering, setReordering] = useState(false);
  const [quickAdd, setQuickAdd] = useState({ name: "", postcode: "" });
  const [quickAdding, setQuickAdding] = useState(false);
  const [traffic, setTraffic] = useState(null);
  const [trafficLoading, setTrafficLoading] = useState(false);
  const [showFlow, setShowFlow] = useState(true);
  const [reroute, setReroute] = useState(null);
  const [rerouteLoading, setRerouteLoading] = useState(false);
  const [pods, setPods] = useState([]);

  const loadTraffic = (id, force = false) => {
    setTrafficLoading(true);
    api.get(`/routes/${id}/traffic${force ? "?force=true" : ""}`)
      .then((r) => setTraffic(r.data))
      .catch((e) => { setTraffic(null); toast.error(errMsg(e)); })
      .finally(() => setTrafficLoading(false));
  };

  const loadPods = (id) => api.get(`/routes/${id}/proofs`).then((r) => setPods(r.data)).catch(() => {});

  const [proofView, setProofView] = useState(null);
  const [proofImgs, setProofImgs] = useState({});
  const [mapInfo, setMapInfo] = useState(null);

  const openProofs = (s) => {
    const stopPods = pods.filter((p) => p.store_id === s.store_id);
    setProofView({ name: s.name, pods: stopPods });
    stopPods.forEach((p) => {
      if (proofImgs[p.id]) return;
      api.get(`/proofs/${p.id}`, { responseType: "blob" })
        .then((r) => setProofImgs((m) => ({ ...m, [p.id]: URL.createObjectURL(r.data) })))
        .catch(() => setProofImgs((m) => ({ ...m, [p.id]: "error" })));
    });
  };

  const autoRerouted = useRef(null);

  useEffect(() => {
    if (!route?.id) { setTraffic(null); setReroute(null); setPods([]); return; }
    setReroute(null);
    setTrafficLoading(true);
    api.get(`/routes/${route.id}/traffic`)
      .then((r) => {
        setTraffic(r.data);
        if (editable && r.data?.live?.delay_s >= 600 && route.stops.length >= 2 && autoRerouted.current !== route.id) {
          autoRerouted.current = route.id;
          suggestReroute(true);
        }
      })
      .catch(() => setTraffic(null))
      .finally(() => setTrafficLoading(false));
    loadPods(route.id);
  }, [route?.id]);

  const suggestReroute = async (auto = false) => {
    setRerouteLoading(true);
    try {
      const { data } = await api.post(`/routes/${route.id}/traffic-reroute`);
      setReroute(data);
      if (data.already_optimal && auto !== true) toast.success("Current stop order is already the fastest in live traffic");
    } catch (e) {
      if (auto !== true) toast.error(errMsg(e));
    } finally {
      setRerouteLoading(false);
    }
  };

  const applyReroute = async () => {
    try {
      const { data } = await api.post(`/routes/${route.id}/reorder`, { store_ids: reroute.suggested_ids });
      setRoute(data);
      setReroute(null);
      loadRoutes(date);
      loadTraffic(data.id, true);
      toast.success("Stop order updated for current traffic");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const moveStop = async (i, dir) => {
    const j = i + dir;
    if (!route || j < 0 || j >= route.stops.length || reordering) return;
    const ids = route.stops.map((s) => s.store_id);
    [ids[i], ids[j]] = [ids[j], ids[i]];
    setReordering(true);
    try {
      const { data } = await api.post(`/routes/${route.id}/reorder`, { store_ids: ids });
      setRoute(data);
      loadRoutes(date);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setReordering(false);
    }
  };

  const onDropStop = async (targetIdx) => {
    if (dragIdx === null || dragIdx === targetIdx || !route) return setDragIdx(null);
    const ids = route.stops.map((s) => s.store_id);
    const [moved] = ids.splice(dragIdx, 1);
    ids.splice(targetIdx, 0, moved);
    setDragIdx(null);
    setReordering(true);
    try {
      const { data } = await api.post(`/routes/${route.id}/reorder`, { store_ids: ids });
      setRoute(data);
      loadRoutes(date);
      toast.success(`Custom order applied — ${fmtDist(data.total_distance_m)}, ${fmtDur(data.total_duration_s)}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setReordering(false);
    }
  };

  const shareRoute = async () => {
    if (!route) return;
    try {
      const { data } = await api.post(`/routes/${route.id}/share`);
      const url = `${window.location.origin}/r/${data.token}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Driver link copied — send it to your driver");
      } catch {
        window.prompt("Driver link (copy it):", url);
      }
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const loadRoutes = (d) => api.get(`/routes?date=${d}`).then((r) => setSavedRoutes(r.data));

  useEffect(() => {
    api.get("/settings/yard").then((r) => {
      setYard(r.data);
      setYardForm({ name: r.data.name || "", postcode: r.data.postcode || "" });
    });
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
    api.get("/stores").then((r) => setStores(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadRoutes(date); }, [date]);

  useEffect(() => {
    const t = setInterval(() => {
      loadRoutes(date);
      if (route?.id) {
        api.get(`/routes/${route.id}`).then((r) => setRoute(r.data)).catch(() => {});
        loadPods(route.id);
      }
    }, 15000);
    return () => clearInterval(t);
  }, [date, route?.id]);

  const saveYard = async () => {
    if (!yardForm.postcode.trim()) return toast.error("Enter your yard postcode");
    setSavingYard(true);
    try {
      const { data } = await api.put("/settings/yard", { name: yardForm.name || "Yard", postcode: yardForm.postcode });
      setYard(data);
      toast.success(`Yard set to ${data.postcode}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSavingYard(false);
    }
  };

  const toggleStore = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const optimize = async () => {
    if (!vehicleId) return toast.error("Select a vehicle");
    if (selected.length === 0) return toast.error("Select at least one store");
    setOptimizing(true);
    try {
      const { data } = await api.post("/routes/optimize", { date, vehicle_id: vehicleId, store_ids: selected });
      setRoute(data);
      loadRoutes(date);
      toast.success(`Route optimised — ${fmtDist(data.total_distance_m)}, ${fmtDur(data.total_duration_s)}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setOptimizing(false);
    }
  };

  const viewRoute = async (id) => {
    const { data } = await api.get(`/routes/${id}`);
    setRoute(data);
  };

  const deleteRoute = async (id) => {
    if (!window.confirm("Delete this route plan?")) return;
    await api.delete(`/routes/${id}`);
    if (route?.id === id) setRoute(null);
    loadRoutes(date);
  };

  const shownStores = useMemo(
    () => stores.filter((s) => `${s.name} ${s.postcode}`.toLowerCase().includes(storeFilter.toLowerCase())),
    [stores, storeFilter]
  );

  useEffect(() => {
    const t = storeFilter.trim();
    if (shownStores.length === 0 && t) {
      if (/^[A-Z]{1,2}[0-9][0-9A-Z]?\s*[0-9]?[A-Z]{0,2}$/i.test(t)) setQuickAdd((q) => ({ ...q, postcode: t.toUpperCase() }));
      else setQuickAdd((q) => ({ ...q, name: t }));
    }
  }, [storeFilter, shownStores.length]);

  const quickAddStore = async () => {
    if (!quickAdd.name.trim() || !quickAdd.postcode.trim()) return toast.error("Enter the store name and postcode");
    setQuickAdding(true);
    try {
      const { data } = await api.post("/stores", { name: quickAdd.name.trim(), postcode: quickAdd.postcode.trim() });
      setStores((s) => [...s, data].sort((a, b) => a.name.localeCompare(b.name)));
      setSelected((sel) => [...sel, data.id]);
      setStoreFilter("");
      setQuickAdd({ name: "", postcode: "" });
      toast.success(`"${data.name}" saved to Stores and selected for this route`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setQuickAdding(false);
    }
  };

  const yardSet = yard?.lat != null;
  const inputCls = "rounded-none bg-background border-border h-9 text-sm";

  return (
    <div className="p-8" data-testid="route-planner-page">
      <div className="mb-8">
        <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter">ROUTE PLANNER</h1>
        <p className="text-muted-foreground text-sm mt-1">Pick a vehicle, add stores — get the optimal delivery loop from your yard and back.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="space-y-4">
          <div className="border border-border bg-card p-5" data-testid="yard-settings-panel">
            <div className="flex items-center gap-2 uppercase text-xs tracking-[0.2em] font-bold text-muted-foreground mb-3">
              <Warehouse size={14} /> Yard (start / return point)
            </div>
            <div className="flex gap-2">
              <Input data-testid="yard-name-input" placeholder="Yard name" value={yardForm.name} onChange={(e) => setYardForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} />
              <Input data-testid="yard-postcode-input" placeholder="Postcode" value={yardForm.postcode} onChange={(e) => setYardForm((f) => ({ ...f, postcode: e.target.value.toUpperCase() }))} className={`${inputCls} w-32 font-mono`} />
              {editable && (
              <Button data-testid="save-yard-button" onClick={saveYard} disabled={savingYard} variant="outline" className="rounded-none border-border hover:bg-muted hover:text-foreground h-9 text-xs uppercase tracking-widest font-bold bg-transparent text-foreground">
                {savingYard ? "…" : "Set"}
              </Button>
              )}
            </div>
            {yardSet ? (
              <div className="text-xs text-emerald-600 dark:text-[#32D74B] mt-2">✓ {yard.name || "Yard"} — {yard.postcode}</div>
            ) : (
              <div className="text-xs text-amber-600 dark:text-[#FFD60A] mt-2">Set your yard postcode before planning routes.</div>
            )}
          </div>

          {editable && (
          <div className="border border-border bg-card p-5 space-y-4">
            <div>
              <Label className="uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground">Date</Label>
              <Input data-testid="route-date-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} className={`${inputCls} mt-1.5`} />
            </div>
            <div>
              <Label className="uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground">Vehicle</Label>
              <Select value={vehicleId} onValueChange={setVehicleId}>
                <SelectTrigger data-testid="route-vehicle-select" className={`${inputCls} mt-1.5`}><SelectValue placeholder="Select vehicle" /></SelectTrigger>
                <SelectContent className="bg-card border-border text-foreground rounded-none">
                  {vehicles.map((v) => (
                    <SelectItem key={v.id} value={v.id}>{v.registration}{v.driver_name ? ` — ${v.driver_name}` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground">Stores ({selected.length} selected)</Label>
                {selected.length > 0 && (
                  <button data-testid="clear-stores-button" onClick={() => setSelected([])} className="text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground">Clear</button>
                )}
              </div>
              <Input data-testid="store-filter-input" placeholder="Filter stores…" value={storeFilter} onChange={(e) => setStoreFilter(e.target.value)} className={`${inputCls} mt-1.5 mb-2`} />
              <div className="max-h-56 overflow-y-auto border border-border divide-y divide-white/5" data-testid="store-picker-list">
                {shownStores.map((s) => (
                  <label key={s.id} className="flex items-center gap-3 px-3 py-2 hover:bg-muted/60 cursor-pointer">
                    <Checkbox data-testid={`store-checkbox-${s.name}`} checked={selected.includes(s.id)} onCheckedChange={() => toggleStore(s.id)} className="rounded-none border-border data-[state=checked]:bg-[#FF3B30] data-[state=checked]:border-[#FF3B30]" />
                    <span className="text-sm flex-1">{s.name}</span>
                    <span className="font-mono text-xs text-muted-foreground">{s.postcode}</span>
                  </label>
                ))}
                {shownStores.length === 0 && (
                  <div className="px-3 py-3 space-y-2" data-testid="quick-add-store-form">
                    <div className="text-xs text-muted-foreground/70">
                      {stores.length === 0 ? "No stores yet — add your first one:" : "Not in your stores — add it now and it'll be saved + selected:"}
                    </div>
                    <Input
                      data-testid="quick-add-name-input"
                      placeholder="Store name"
                      value={quickAdd.name}
                      onChange={(e) => setQuickAdd((q) => ({ ...q, name: e.target.value }))}
                      className={inputCls}
                    />
                    <div className="flex gap-2">
                      <Input
                        data-testid="quick-add-postcode-input"
                        placeholder="Postcode"
                        value={quickAdd.postcode}
                        onChange={(e) => setQuickAdd((q) => ({ ...q, postcode: e.target.value.toUpperCase() }))}
                        className={`${inputCls} font-mono flex-1`}
                      />
                      <Button
                        data-testid="quick-add-store-button"
                        onClick={quickAddStore}
                        disabled={quickAdding}
                        className="rounded-none bg-[#FF3B30] hover:bg-[#ff554b] font-bold uppercase tracking-widest text-[10px] h-9 px-4"
                      >
                        {quickAdding ? "Geocoding…" : "+ Add store"}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <Button data-testid="optimize-route-button" onClick={optimize} disabled={optimizing || !yardSet} className="w-full rounded-none bg-[#FF3B30] hover:bg-[#ff554b] font-bold uppercase tracking-widest text-xs h-11">
              <Navigation size={14} className="mr-1.5" /> {optimizing ? "Optimising…" : "Optimise route"}
            </Button>
          </div>
          )}

          <div className="border border-border bg-card" data-testid="saved-routes-panel">
            <div className="px-5 py-3 border-b border-border uppercase text-xs tracking-[0.2em] font-bold text-muted-foreground">Routes on {date}</div>
            {savedRoutes.length === 0 && <div className="px-5 py-4 text-xs text-muted-foreground/70">No routes planned for this date.</div>}
            {savedRoutes.map((r) => (
              <div key={r.id} className={`px-5 py-3 border-b border-border/60 flex items-center justify-between hover:bg-muted/60 ${route?.id === r.id ? "bg-muted/60" : ""}`}>
                <button data-testid={`view-route-${r.vehicle_reg}`} onClick={() => viewRoute(r.id)} className="text-left flex-1">
                  <div className="font-mono font-bold text-sm">{r.vehicle_reg}</div>
                  <div className="text-xs text-muted-foreground">{r.stops.length} stops · {fmtDist(r.total_distance_m)} · {fmtDur(r.total_duration_s)}</div>
                  {(() => {
                    const d = r.stops.filter((s) => s.delivery_status === "delivered").length;
                    const fl = r.stops.filter((s) => s.delivery_status === "failed").length;
                    if (d === 0 && fl === 0) return null;
                    return (
                      <div className="text-[10px] uppercase tracking-widest font-bold mt-0.5" data-testid={`route-progress-${r.vehicle_reg}`}>
                        <span className="text-emerald-600 dark:text-[#32D74B]">✓ {d}/{r.stops.length} delivered</span>
                        {fl > 0 && <span className="text-[#FF3B30] ml-2">✗ {fl} failed</span>}
                      </div>
                    );
                  })()}
                </button>
                <button data-testid={`print-route-${r.vehicle_reg}`} onClick={() => window.open(`/route-sheet/${r.id}`, "_blank")} className="text-muted-foreground/70 hover:text-foreground mr-2" title="Print route sheet"><Printer size={14} /></button>
                {editable && (
                <button data-testid={`delete-route-${r.vehicle_reg}`} onClick={() => deleteRoute(r.id)} className="text-muted-foreground/70 hover:text-[#FF3B30]"><Trash2 size={14} /></button>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2 space-y-4">
          <div className="border border-border bg-card h-[420px] relative" data-testid="route-map">
            <button
              data-testid="traffic-toggle-button"
              onClick={() => setShowFlow((s) => !s)}
              className={`absolute top-3 right-3 z-[20] inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold border transition-colors duration-150 ${showFlow ? "bg-[#FF3B30] text-white border-[#FF3B30]" : "bg-background text-muted-foreground border-border hover:text-foreground"}`}
              title="Live traffic overlay (green = flowing, red = heavy)"
            >
              <TrafficCone size={12} /> Traffic {showFlow ? "on" : "off"}
            </button>
            <div
              data-testid="map-legend"
              className="absolute bottom-3 left-3 z-[20] bg-background/90 backdrop-blur-sm border border-border px-3 py-2 text-[10px] leading-relaxed text-foreground"
            >
              <div className="flex items-center gap-1.5"><span className="inline-block w-5 h-1 rounded" style={{ background: "#0A84FF" }} /> <b>Your route (clear)</b></div>
              {traffic?.jam_segments?.length > 0 && (
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="inline-flex w-5 h-1 rounded overflow-hidden">
                    <span className="flex-1" style={{ background: "#f59e0b" }} />
                    <span className="flex-1" style={{ background: "#ef4444" }} />
                  </span>
                  <b>Traffic on your route</b>
                </div>
              )}
              {showFlow && (
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="inline-flex w-5 h-1 rounded overflow-hidden">
                    <span className="flex-1" style={{ background: "#22c55e" }} />
                    <span className="flex-1" style={{ background: "#f59e0b" }} />
                    <span className="flex-1" style={{ background: "#ef4444" }} />
                  </span>
                  Traffic: green flowing · red jammed
                </div>
              )}
              {traffic?.incidents?.length > 0 && (
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="inline-block w-3 h-3 rounded-sm text-white font-mono font-bold text-[8px] text-center leading-3" style={{ background: "#b91c1c" }}>C</span>
                  Warnings: C closed · A accident · J jam · W works
                </div>
              )}
            </div>
            <GoogleMapShell theme={theme} interactive>
              {showFlow && <GTrafficLayer />}
              {traffic?.incidents?.map((inc, i) => (
                <AdvancedMarker
                  key={`inc-${i}`}
                  position={{ lat: inc.lat, lng: inc.lon }}
                  onClick={() =>
                    setMapInfo({
                      pos: { lat: inc.lat, lng: inc.lon },
                      node: (
                        <div className="text-xs text-neutral-900">
                          <b>{inc.category}</b>{inc.road ? ` · ${inc.road}` : ""}<br />
                          {inc.description}
                          {inc.from && <><br />{inc.from} → {inc.to}</>}
                          {inc.delay_s ? <><br />Delay ≈ {Math.round(inc.delay_s / 60)} min</> : null}
                        </div>
                      ),
                    })
                  }
                >
                  <WarnPin cat={inc.icon_category} />
                </AdvancedMarker>
              ))}
              {route && (
                <>
                  <FitBounds points={route.geometry} padding={30} />
                  <GPolyline path={route.geometry} color="#0A84FF" weight={5} opacity={0.95} />
                  {traffic?.jam_segments?.map((seg, i) => (
                    <GPolyline
                      key={`jam-${i}`}
                      path={seg.points}
                      color={seg.category === "ROAD_CLOSURE" ? "#7f1d1d" : (seg.magnitude || 0) >= 3 ? "#ef4444" : "#f59e0b"}
                      weight={6}
                      opacity={0.95}
                      zIndex={3}
                    />
                  ))}
                  {route.driver_location && Date.now() - new Date(route.driver_location.updated_at).getTime() < 2 * 3600 * 1000 && (
                    <AdvancedMarker
                      position={{ lat: route.driver_location.lat, lng: route.driver_location.lon }}
                      onClick={() =>
                        setMapInfo({
                          pos: { lat: route.driver_location.lat, lng: route.driver_location.lon },
                          node: (
                            <div className="text-xs text-neutral-900">
                              <b>{route.driver_name || "Driver"}</b> — live location<br />
                              Updated {new Date(route.driver_location.updated_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                            </div>
                          ),
                        })
                      }
                    >
                      <VanPin />
                    </AdvancedMarker>
                  )}
                  <AdvancedMarker
                    position={{ lat: route.yard.lat, lng: route.yard.lon }}
                    onClick={() => setMapInfo({ pos: { lat: route.yard.lat, lng: route.yard.lon }, node: <div className="text-xs text-neutral-900">{route.yard.name || "Yard"} — {route.yard.postcode}</div> })}
                  >
                    <NumPin label="Y" bg="#111" />
                  </AdvancedMarker>
                  {route.stops.map((s) => (
                    <AdvancedMarker
                      key={s.store_id}
                      position={{ lat: s.lat, lng: s.lon }}
                      onClick={() => setMapInfo({ pos: { lat: s.lat, lng: s.lon }, node: <div className="text-xs text-neutral-900">{s.order}. {s.name} ({s.postcode})</div> })}
                    >
                      <NumPin label={s.order} bg="#FF3B30" />
                    </AdvancedMarker>
                  ))}
                </>
              )}
              {!route && yardSet && (
                <AdvancedMarker position={{ lat: yard.lat, lng: yard.lon }}>
                  <NumPin label="Y" bg="#111" />
                </AdvancedMarker>
              )}
              {mapInfo && (
                <InfoWindow position={mapInfo.pos} onCloseClick={() => setMapInfo(null)}>
                  {mapInfo.node}
                </InfoWindow>
              )}
            </GoogleMapShell>
          </div>

          {route && (
            <div className="border border-border bg-card" data-testid="route-result-panel">
              <div className="px-5 py-4 border-b border-border flex items-center justify-between flex-wrap gap-2">
                <div>
                  <span className="font-mono font-bold">{route.vehicle_reg}</span>
                  {route.driver_name && <span className="text-muted-foreground text-sm"> · {route.driver_name}</span>}
                  <span className="text-muted-foreground text-sm"> · {route.date}</span>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground/70 mt-0.5">{route.custom_order ? "Custom order — drag stops to adjust" : "Optimised — drag stops to set your own order"}</div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="font-heading font-black tracking-tighter text-xl">
                    {fmtDist(route.total_distance_m)} <span className="text-muted-foreground">·</span> {fmtDur(route.total_duration_s)}
                  </div>
                  {editable && (
                  <button
                    data-testid="share-route-button"
                    onClick={shareRoute}
                    className="inline-flex items-center gap-1.5 border border-border px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold hover:bg-muted transition-colors duration-150"
                  >
                    <Share2 size={13} /> Driver link
                  </button>
                  )}
                  <button
                    data-testid="print-route-sheet-button"
                    onClick={() => window.open(`/route-sheet/${route.id}`, "_blank")}
                    className="inline-flex items-center gap-1.5 border border-border px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold hover:bg-muted transition-colors duration-150"
                  >
                    <Printer size={13} /> Route sheet
                  </button>
                </div>
              </div>
              {(traffic || trafficLoading) && (
                <div className="px-5 py-3 border-b border-border bg-muted/30" data-testid="traffic-panel">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2 uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground">
                      <TrafficCone size={13} className="text-[#FF3B30]" /> Live traffic
                      {trafficLoading && <span className="normal-case font-normal tracking-normal">checking…</span>}
                    </div>
                    <div className="flex items-center gap-3">
                      {editable && route.stops.length >= 2 && (
                        <button
                          data-testid="suggest-reroute-button"
                          onClick={() => suggestReroute()}
                          disabled={rerouteLoading || trafficLoading}
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-[#FF3B30] hover:underline disabled:opacity-50"
                        >
                          {rerouteLoading ? "Checking…" : "Suggest faster order"}
                        </button>
                      )}
                      <button
                        data-testid="refresh-traffic-button"
                        onClick={() => loadTraffic(route.id, true)}
                        disabled={trafficLoading}
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-muted-foreground hover:text-foreground transition-colors duration-150"
                      >
                        <RefreshCw size={11} className={trafficLoading ? "animate-spin" : ""} /> Refresh
                      </button>
                    </div>
                  </div>
                  {reroute && !reroute.already_optimal && (
                    <div className="mt-2 border border-[#FF3B30]/40 bg-[#FF3B30]/5 p-3" data-testid="reroute-suggestion">
                      <div className="text-xs font-bold">
                        Faster order found in current traffic
                        {reroute.saving_s > 0 && <span className="text-emerald-600 dark:text-[#32D74B]"> — saves ~{Math.round(reroute.saving_s / 60)} min</span>}
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        {reroute.suggested_order.map((s, i) => `${i + 1}. ${s.name}`).join("  →  ")}
                      </div>
                      <div className="mt-2 flex gap-2">
                        <button data-testid="apply-reroute-button" onClick={applyReroute} className="bg-[#FF3B30] hover:bg-[#ff554b] text-white px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest">Apply this order</button>
                        <button data-testid="dismiss-reroute-button" onClick={() => setReroute(null)} className="border border-border px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Dismiss</button>
                      </div>
                    </div>
                  )}
                  {traffic?.live?.travel_time_s != null && (
                    <div className="mt-2 text-sm" data-testid="traffic-eta">
                      <span className="font-bold">{fmtDur(traffic.live.travel_time_s)}</span>
                      <span className="text-muted-foreground"> drive in current traffic</span>
                      {traffic.live.delay_s > 60 ? (
                        <span className={`ml-2 font-bold ${traffic.live.delay_s >= 900 ? "text-[#FF3B30]" : "text-amber-600 dark:text-[#FFD60A]"}`}>
                          +{Math.round(traffic.live.delay_s / 60)} min delay
                        </span>
                      ) : (
                        <span className="ml-2 font-bold text-emerald-600 dark:text-[#32D74B]">no significant delay</span>
                      )}
                    </div>
                  )}
                  {traffic && traffic.incidents.length === 0 && (
                    <div className="mt-1.5 text-xs text-emerald-600 dark:text-[#32D74B]" data-testid="traffic-clear">No incidents reported along this route.</div>
                  )}
                  {traffic && traffic.incidents.length > 0 && (
                    <div className="mt-2 space-y-1.5">
                      {traffic.incidents.slice(0, 8).map((inc, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs" data-testid={`traffic-warning-${i}`}>
                          <span
                            className="mt-0.5 w-4 h-4 flex items-center justify-center text-white font-mono font-bold text-[9px] shrink-0 rounded-sm"
                            style={{ background: WARN_COLORS[inc.icon_category] || "#f59e0b" }}
                          >
                            {WARN_LETTER[inc.icon_category] || "!"}
                          </span>
                          <span className="min-w-0">
                            <span className="font-bold">{inc.category}</span>
                            {inc.road && <span className="font-mono"> {inc.road}</span>}
                            {inc.from && <span className="text-muted-foreground"> — {inc.from}{inc.to ? ` → ${inc.to}` : ""}</span>}
                            {inc.delay_s ? <span className="text-[#FF3B30] font-bold"> +{Math.round(inc.delay_s / 60)}m</span> : null}
                          </span>
                        </div>
                      ))}
                      {traffic.incidents.length > 8 && (
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">+{traffic.incidents.length - 8} more shown on the map</div>
                      )}
                    </div>
                  )}
                </div>
              )}
              <div className="divide-y divide-white/5">
                <div className="px-5 py-2.5 flex items-center gap-3 text-sm">
                  <span className="w-7 h-7 flex items-center justify-center bg-muted border border-border font-mono font-bold text-xs shrink-0">Y</span>
                  <span className="font-semibold">{route.yard.name || "Yard"}</span>
                  <span className="font-mono text-xs text-muted-foreground">{route.yard.postcode}</span>
                  <span className="ml-auto text-xs text-muted-foreground">START</span>
                </div>
                {route.stops.map((s, i) => (
                  <div
                    key={s.store_id}
                    data-testid={`route-stop-${s.order}`}
                    draggable
                    onDragStart={() => setDragIdx(i)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => onDropStop(i)}
                    onDragEnd={() => setDragIdx(null)}
                    className={`px-5 py-2.5 flex items-center gap-3 text-sm cursor-grab active:cursor-grabbing ${dragIdx === i ? "opacity-40" : ""} ${reordering ? "pointer-events-none opacity-60" : ""}`}
                  >
                    <span className="flex flex-col -my-1 shrink-0">
                      <button
                        data-testid={`move-up-${s.order}`}
                        disabled={i === 0 || reordering}
                        onClick={(e) => { e.stopPropagation(); moveStop(i, -1); }}
                        className="text-muted-foreground/60 hover:text-foreground disabled:opacity-20 p-0.5"
                        title="Move up"
                      >
                        <ChevronUp size={13} />
                      </button>
                      <button
                        data-testid={`move-down-${s.order}`}
                        disabled={i === route.stops.length - 1 || reordering}
                        onClick={(e) => { e.stopPropagation(); moveStop(i, 1); }}
                        className="text-muted-foreground/60 hover:text-foreground disabled:opacity-20 p-0.5"
                        title="Move down"
                      >
                        <ChevronDown size={13} />
                      </button>
                    </span>
                    <GripVertical size={14} className="text-muted-foreground/60 shrink-0" />
                    <span className="w-7 h-7 flex items-center justify-center bg-[#FF3B30] text-white font-mono font-bold text-xs shrink-0">{s.order}</span>
                    <span className="font-semibold">{s.name}</span>
                    <span className="font-mono text-xs text-muted-foreground">{s.postcode}</span>
                    <span className="ml-auto flex items-center gap-3">
                      {pods.filter((p) => p.store_id === s.store_id).map((p) => (
                        <button
                          key={p.id}
                          data-testid={`planner-proof-${p.id}`}
                          onClick={(e) => { e.stopPropagation(); openProofs(s); }}
                          title={p.kind === "photo" ? "View delivery photo" : "View customer signature"}
                          className="text-muted-foreground hover:text-[#FF3B30] transition-colors duration-150"
                        >
                          {p.kind === "photo" ? <Camera size={14} /> : <PenLine size={14} />}
                        </button>
                      ))}
                      {s.delivery_status === "delivered" && (
                        <button
                          onClick={(e) => { e.stopPropagation(); if (pods.some((p) => p.store_id === s.store_id)) openProofs(s); }}
                          className={`text-[10px] uppercase tracking-widest font-bold text-emerald-600 dark:text-[#32D74B] ${pods.some((p) => p.store_id === s.store_id) ? "underline decoration-dotted underline-offset-2 cursor-pointer" : "cursor-default"}`}
                          data-testid={`planner-stop-delivered-${s.order}`}
                        >
                          ✓ {s.delivery_time ? new Date(s.delivery_time).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "Delivered"}
                        </button>
                      )}
                      {s.delivery_status === "failed" && (
                        <span className="text-[10px] uppercase tracking-widest font-bold text-[#FF3B30]" data-testid={`planner-stop-failed-${s.order}`}>
                          ✗ {s.delivery_reason || "Failed"}
                        </span>
                      )}
                      <span className="text-xs text-muted-foreground">+{fmtDist(s.leg_distance_m)} · {fmtDur(s.leg_duration_s)}</span>
                    </span>
                  </div>
                ))}
                <div className="px-5 py-2.5 flex items-center gap-3 text-sm">
                  <span className="w-7 h-7 flex items-center justify-center bg-muted border border-border font-mono font-bold text-xs shrink-0">Y</span>
                  <span className="font-semibold">Return to {route.yard.name || "Yard"}</span>
                  <span className="ml-auto text-xs text-muted-foreground">+{fmtDist(route.return_leg.distance_m)} · {fmtDur(route.return_leg.duration_s)}</span>
                </div>
              </div>
            </div>
          )}
          {!route && (
            <div className="border border-border bg-card p-10 text-center text-muted-foreground/70 text-sm">
              Optimised route details will appear here.
            </div>
          )}
        </div>
      </div>

      <Dialog open={!!proofView} onOpenChange={(o) => !o && setProofView(null)}>
        <DialogContent className="bg-card border-border rounded-none text-foreground max-w-2xl" data-testid="proof-viewer-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-black tracking-tighter uppercase">Proof of delivery — {proofView?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 max-h-[70vh] overflow-y-auto pr-1">
            {proofView?.pods.length === 0 && (
              <div className="py-8 text-center text-muted-foreground/70 text-sm" data-testid="proof-viewer-empty">No photo or signature uploaded for this stop yet.</div>
            )}
            {proofView?.pods.map((p) => (
              <div key={p.id} data-testid={`proof-viewer-item-${p.id}`}>
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-1.5">
                  {p.kind === "photo" ? <Camera size={12} /> : <PenLine size={12} />}
                  {p.kind === "photo" ? "Delivery photo" : "Customer signature"}
                  {p.created_at && <span className="ml-auto font-mono normal-case tracking-normal">{new Date(p.created_at).toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>}
                </div>
                {proofImgs[p.id] === "error" ? (
                  <div className="h-32 flex items-center justify-center text-[#FF3B30] text-sm border border-border">Could not load image</div>
                ) : proofImgs[p.id] ? (
                  <img src={proofImgs[p.id]} alt={p.kind} className={`w-full border border-border ${p.kind === "signature" ? "bg-white" : ""}`} data-testid={`proof-image-${p.id}`} />
                ) : (
                  <div className="h-32 flex items-center justify-center text-muted-foreground/70 text-sm border border-border animate-pulse">Loading…</div>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
